/**
 * SchoolCommunicationEngine — Phase E
 * Tabs: Templates · Queue · Triggers · HR Digest · Analytics
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Loader2, Plus, Trash2, Send, RefreshCw, Zap, Mail, MessageSquare, Radio,
  FileText, BarChart3, AlertTriangle, Sparkles, CalendarClock, Users, Check,
  Clock, ShieldOff, UserSearch
} from 'lucide-react';
import api from '@/lib/api';

const TRIGGERS = [
  { value: 'attendance_alert', label: 'Attendance Alert' },
  { value: 'performance_warning', label: 'Performance Warning' },
  { value: 'contract_expiry', label: 'Contract Expiry' },
  { value: 'hr_weekly', label: 'HR Weekly Digest' },
  { value: 'weekly_parent_digest', label: 'Parent Weekly Digest' },
  { value: 'custom', label: 'Custom' },
];
const CHANNELS = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
  { value: 'whatsapp', label: 'WhatsApp', icon: Radio },
  { value: 'all', label: 'All channels', icon: Send },
];

const STATUS_COLOR = {
  queued: 'bg-blue-100 text-blue-800',
  sent: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-rose-100 text-rose-800',
};

// ========== TEMPLATES ==========
const TemplatesTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', trigger: 'custom', channel: 'email', subject: '', body: '', is_active: true });
  const [editId, setEditId] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ['comm-templates', tenantId], queryFn: () => api.get(`/api/school/tenants/${tenantId}/comm/templates`).then(r => r.data) });

  const saveMut = useMutation({
    mutationFn: (body) => editId ? api.put(`/api/school/tenants/${tenantId}/comm/templates/${editId}`, body) : api.post(`/api/school/tenants/${tenantId}/comm/templates`, body),
    onSuccess: () => { toast.success('Template saved'); setOpen(false); setEditId(null); qc.invalidateQueries({ queryKey: ['comm-templates', tenantId] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/api/school/tenants/${tenantId}/comm/templates/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['comm-templates', tenantId] }); },
  });

  const openNew = () => { setEditId(null); setForm({ name: '', trigger: 'custom', channel: 'email', subject: '', body: '', is_active: true }); setOpen(true); };
  const openEdit = (t) => { setEditId(t.id); setForm({ name: t.name, trigger: t.trigger, channel: t.channel, subject: t.subject || '', body: t.body, is_active: t.is_active }); setOpen(true); };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><FileText className="h-4 w-4 text-indigo-600" /> Message Templates</CardTitle>
          <CardDescription>Use <code>{'{{student_name}}'}</code>, <code>{'{{attendance_percent}}'}</code>, <code>{'{{school_name}}'}</code> as variables.</CardDescription>
        </div>
        <Button onClick={openNew} data-testid="comm-new-template-btn"><Plus className="h-4 w-4 mr-1" /> New Template</Button>
      </CardHeader>
      <CardContent>
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> :
         data.length === 0 ? <p className="text-sm text-slate-500 text-center py-6" data-testid="comm-templates-empty">No templates yet.</p> :
         <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Trigger</TableHead><TableHead>Channel</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
           {data.map(t => (
            <TableRow key={t.id} data-testid={`comm-template-${t.id}`}>
              <TableCell className="font-medium">{t.name}</TableCell>
              <TableCell><Badge variant="outline">{t.trigger}</Badge></TableCell>
              <TableCell><Badge variant="secondary">{t.channel}</Badge></TableCell>
              <TableCell>{t.is_active ? <Check className="h-4 w-4 text-emerald-600" /> : '—'}</TableCell>
              <TableCell className="text-right space-x-1">
                <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`comm-edit-template-${t.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => { if (confirm('Delete template?')) delMut.mutate(t.id); }} data-testid={`comm-del-template-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </TableCell>
            </TableRow>
           ))}
          </TableBody>
         </Table>}
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editId ? 'Edit' : 'New'} Template</DialogTitle>
          <DialogDescription>Design a reusable message with <code>{'{{variables}}'}</code>.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="comm-tpl-name" /></div>
            <div><Label>Trigger</Label>
              <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                <SelectTrigger data-testid="comm-tpl-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>{TRIGGERS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Channel</Label>
              <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                <SelectTrigger data-testid="comm-tpl-channel"><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNELS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(form.channel === 'email' || form.channel === 'all') && (
              <div className="md:col-span-2"><Label>Subject (email only)</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="comm-tpl-subject" /></div>
            )}
            <div className="md:col-span-2"><Label>Body *</Label><Textarea rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Dear {{student_name}}, your attendance is {{attendance_percent}}%." data-testid="comm-tpl-body" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate(form)} disabled={!form.name || !form.body || saveMut.isPending} data-testid="comm-tpl-save">
              {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ========== QUEUE ==========
const QueueTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [channel, setChannel] = useState('');
  const qs = [status && `status=${status}`, channel && `channel=${channel}`].filter(Boolean).join('&');
  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ['comm-queue', tenantId, status, channel],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/comm/queue${qs ? '?' + qs : ''}`).then(r => r.data),
    refetchInterval: 10000,
  });
  const processMut = useMutation({
    mutationFn: () => api.post(`/api/school/tenants/${tenantId}/comm/queue/process`),
    onSuccess: (r) => { toast.success(`Processed ${r.data.processed} · ${r.data.sent} sent · ${r.data.failed} failed`); qc.invalidateQueries({ queryKey: ['comm-queue', tenantId] }); qc.invalidateQueries({ queryKey: ['comm-analytics', tenantId] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });
  const retryMut = useMutation({
    mutationFn: () => api.post(`/api/school/tenants/${tenantId}/comm/queue/retry-failed`),
    onSuccess: (r) => { toast.success(`${r.data.requeued} re-queued`); qc.invalidateQueries({ queryKey: ['comm-queue', tenantId] }); },
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/api/school/tenants/${tenantId}/comm/queue/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comm-queue', tenantId] }),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><Send className="h-4 w-4 text-emerald-600" /> Message Queue</CardTitle>
          <CardDescription>Auto-refreshes every 10s. Process button drains queued + retries failed up to 3 times.</CardDescription>
        </div>
        <div className="flex gap-2">
          <Select value={status || 'all'} onValueChange={(v) => setStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-32" data-testid="queue-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={channel || 'all'} onValueChange={(v) => setChannel(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-32" data-testid="queue-channel-filter"><SelectValue placeholder="Channel" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="sms">SMS</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => refetch()} data-testid="queue-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          <Button variant="outline" onClick={() => retryMut.mutate()} disabled={retryMut.isPending} data-testid="queue-retry-btn">
            {retryMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />} Retry Failed
          </Button>
          <Button onClick={() => processMut.mutate()} disabled={processMut.isPending} data-testid="queue-process-btn">
            {processMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Process Queue
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> :
         data.length === 0 ? <p className="text-sm text-slate-500 text-center py-6" data-testid="queue-empty">Queue is empty.</p> :
         <Table>
          <TableHeader><TableRow><TableHead>To</TableHead><TableHead>Channel</TableHead><TableHead>Trigger</TableHead><TableHead>Subject</TableHead><TableHead>Status</TableHead><TableHead>Retries</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
           {data.slice(0, 100).map(r => (
            <TableRow key={r.id} data-testid={`queue-row-${r.id}`}>
              <TableCell className="text-xs">{r.to_email || r.to_phone}</TableCell>
              <TableCell><Badge variant="secondary">{r.channel}</Badge></TableCell>
              <TableCell><Badge variant="outline" className="text-xs">{r.trigger}</Badge></TableCell>
              <TableCell className="text-xs truncate max-w-[200px]">{r.subject || '—'}</TableCell>
              <TableCell><Badge className={STATUS_COLOR[r.status] || ''}>{r.status}</Badge>{r.last_error && <div className="text-[10px] text-rose-600 mt-1 truncate max-w-[180px]" title={r.last_error}>{r.last_error}</div>}</TableCell>
              <TableCell className="text-xs">{r.retry_count || 0}</TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" onClick={() => delMut.mutate(r.id)} data-testid={`queue-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </TableCell>
            </TableRow>
           ))}
          </TableBody>
         </Table>}
      </CardContent>
    </Card>
  );
};

// ========== TRIGGERS ==========
const TriggersTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const [attCfg, setAttCfg] = useState({ threshold_attendance: 80, channel: 'email' });
  const [perfCfg, setPerfCfg] = useState({ threshold_performance: 50, channel: 'email' });
  const [contractCfg, setContractCfg] = useState({ contract_days_ahead: 15, channel: 'email' });

  const runMut = useMutation({
    mutationFn: ({ slug, body }) => api.post(`/api/school/tenants/${tenantId}/comm/triggers/${slug}`, body),
    onSuccess: (r) => { toast.success(`${r.data.candidates} candidates · ${r.data.queued} queued${r.data.dry_run ? ' (preview)' : ''}`); qc.invalidateQueries({ queryKey: ['comm-queue', tenantId] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });

  const TriggerCard = ({ title, icon: Icon, color, children, testid, onRun, onDry }) => (
    <Card data-testid={testid}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Icon className={`h-4 w-4 ${color}`} /> {title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {children}
        <div className="flex gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onDry} data-testid={`${testid}-dry`}>Dry-run Preview</Button>
          <Button size="sm" onClick={onRun} data-testid={`${testid}-run`}><Zap className="h-3.5 w-3.5 mr-1" /> Queue Messages</Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="grid md:grid-cols-3 gap-4">
      <TriggerCard title="Attendance Alert" icon={AlertTriangle} color="text-rose-600" testid="trigger-attendance"
        onDry={() => runMut.mutate({ slug: 'attendance-alert', body: { ...attCfg, dry_run: true } })}
        onRun={() => runMut.mutate({ slug: 'attendance-alert', body: { ...attCfg, dry_run: false } })}>
        <div><Label>Threshold %</Label><Input type="number" value={attCfg.threshold_attendance} onChange={(e) => setAttCfg({ ...attCfg, threshold_attendance: Number(e.target.value) })} /></div>
        <div><Label>Channel</Label>
          <Select value={attCfg.channel} onValueChange={(v) => setAttCfg({ ...attCfg, channel: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{CHANNELS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </TriggerCard>

      <TriggerCard title="Performance Warning" icon={BarChart3} color="text-purple-600" testid="trigger-performance"
        onDry={() => runMut.mutate({ slug: 'performance-warning', body: { ...perfCfg, dry_run: true } })}
        onRun={() => runMut.mutate({ slug: 'performance-warning', body: { ...perfCfg, dry_run: false } })}>
        <div><Label>Threshold score</Label><Input type="number" value={perfCfg.threshold_performance} onChange={(e) => setPerfCfg({ ...perfCfg, threshold_performance: Number(e.target.value) })} /></div>
        <div><Label>Channel</Label>
          <Select value={perfCfg.channel} onValueChange={(v) => setPerfCfg({ ...perfCfg, channel: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{CHANNELS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </TriggerCard>

      <TriggerCard title="Contract Expiry" icon={CalendarClock} color="text-amber-600" testid="trigger-contract"
        onDry={() => runMut.mutate({ slug: 'contract-expiry', body: { ...contractCfg, dry_run: true } })}
        onRun={() => runMut.mutate({ slug: 'contract-expiry', body: { ...contractCfg, dry_run: false } })}>
        <div><Label>Days ahead</Label><Input type="number" value={contractCfg.contract_days_ahead} onChange={(e) => setContractCfg({ ...contractCfg, contract_days_ahead: Number(e.target.value) })} /></div>
        <div><Label>Channel</Label>
          <Select value={contractCfg.channel} onValueChange={(v) => setContractCfg({ ...contractCfg, channel: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{CHANNELS.filter(c => c.value !== 'whatsapp').map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </TriggerCard>
    </div>
  );
};

// ========== HR WEEKLY DIGEST ==========
const HRDigestTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const [recipients, setRecipients] = useState('');
  const { data: preview, isLoading } = useQuery({
    queryKey: ['hr-weekly-preview', tenantId],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/comm/hr-weekly-digest/preview`).then(r => r.data),
  });
  const sendMut = useMutation({
    mutationFn: (body) => api.post(`/api/school/tenants/${tenantId}/comm/hr-weekly-digest/send`, body),
    onSuccess: (r) => { toast.success(`${r.data.queued} email(s) queued to ${r.data.recipients?.length || 0} admin(s)`); qc.invalidateQueries({ queryKey: ['comm-queue', tenantId] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });
  const d = preview || {};

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Sparkles className="h-4 w-4 text-indigo-600" /> HR Weekly Digest</CardTitle>
        <CardDescription>Monday-morning admin summary — attendance risks, top/bottom teachers, week's events, high-severity alerts.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <>
            <div className="grid md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg border bg-rose-50"><div className="text-xs text-rose-700">High-sev alerts</div><div className="text-2xl font-bold text-rose-900" data-testid="hr-digest-alerts">{(d.high_severity_alerts || []).length}</div></div>
              <div className="p-3 rounded-lg border bg-indigo-50"><div className="text-xs text-indigo-700">Attendance risks</div><div className="text-2xl font-bold text-indigo-900" data-testid="hr-digest-att">{d.attendance_risk_count || 0}</div></div>
              <div className="p-3 rounded-lg border bg-amber-50"><div className="text-xs text-amber-700">Contracts soon</div><div className="text-2xl font-bold text-amber-900" data-testid="hr-digest-contracts">{(d.contracts_expiring_soon || []).length}</div></div>
              <div className="p-3 rounded-lg border bg-emerald-50"><div className="text-xs text-emerald-700">Anniversaries + B-days</div><div className="text-2xl font-bold text-emerald-900" data-testid="hr-digest-events">{(d.anniversaries_this_week || []).length + (d.birthdays_this_week || []).length}</div></div>
            </div>
            <div>
              <Label>Override recipients (optional, comma-separated emails)</Label>
              <Input placeholder="principal@school.com, hr@school.com" value={recipients} onChange={(e) => setRecipients(e.target.value)} data-testid="hr-digest-recipients" />
              <p className="text-xs text-slate-500 mt-1">Leaves blank = all team members with role admin / principal / owner.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => sendMut.mutate({ recipients: recipients.split(',').map(s => s.trim()).filter(Boolean), dry_run: true })} data-testid="hr-digest-preview-btn">Preview (no send)</Button>
              <Button onClick={() => sendMut.mutate({ recipients: recipients.split(',').map(s => s.trim()).filter(Boolean), dry_run: false })} disabled={sendMut.isPending} data-testid="hr-digest-send-btn">
                {sendMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Queue Digest Emails
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ========== ANALYTICS ==========
const AnalyticsTab = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['comm-analytics', tenantId],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/comm/analytics`).then(r => r.data),
    refetchInterval: 15000,
  });
  const d = data || {};
  const rate = d.delivery_rate_percent || 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="h-4 w-4 text-emerald-600" /> Analytics — last {d.window_days || 30} days</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="p-4 rounded-lg border bg-indigo-50"><div className="text-xs text-indigo-700">Total messages</div><div className="text-3xl font-bold text-indigo-900" data-testid="comm-total">{d.total || 0}</div></div>
              <div className="p-4 rounded-lg border bg-emerald-50"><div className="text-xs text-emerald-700">Delivered</div><div className="text-3xl font-bold text-emerald-900" data-testid="comm-sent">{d.sent || 0}</div></div>
              <div className="p-4 rounded-lg border bg-amber-50"><div className="text-xs text-amber-700">Delivery rate</div><div className="text-3xl font-bold text-amber-900" data-testid="comm-rate">{rate}%</div></div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <h4 className="text-sm font-semibold mb-2">By Status</h4>
                {Object.entries(d.by_status || {}).map(([k, v]) => <div key={k} className="flex justify-between text-xs py-1"><span className="capitalize">{k}</span><Badge className={STATUS_COLOR[k] || ''}>{v}</Badge></div>)}
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-2">By Channel</h4>
                {Object.entries(d.by_channel || {}).map(([k, v]) => <div key={k} className="flex justify-between text-xs py-1"><span className="capitalize">{k}</span><Badge variant="secondary">{v}</Badge></div>)}
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-2">By Trigger</h4>
                {Object.entries(d.by_trigger || {}).map(([k, v]) => <div key={k} className="flex justify-between text-xs py-1"><span>{k}</span><Badge variant="outline">{v}</Badge></div>)}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ========== MAIN ==========
export const SchoolCommunicationEngine = ({ tenantId }) => {
  return (
    <div className="space-y-4" data-testid="comm-engine-panel">
      <div>
        <h2 className="text-base font-semibold flex items-center gap-2"><Send className="h-5 w-5" /> Communication Engine</h2>
        <p className="text-sm text-slate-500">Templates · Queue · Triggers · HR Digest · Analytics</p>
      </div>
      <Tabs defaultValue="triggers">
        <TabsList className="flex-wrap">
          <TabsTrigger value="triggers" data-testid="comm-tab-triggers"><Zap className="h-4 w-4 mr-1" /> Triggers</TabsTrigger>
          <TabsTrigger value="queue" data-testid="comm-tab-queue"><Send className="h-4 w-4 mr-1" /> Queue</TabsTrigger>
          <TabsTrigger value="templates" data-testid="comm-tab-templates"><FileText className="h-4 w-4 mr-1" /> Templates</TabsTrigger>
          <TabsTrigger value="hr-digest" data-testid="comm-tab-hr-digest"><Users className="h-4 w-4 mr-1" /> HR Digest</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="comm-tab-analytics"><BarChart3 className="h-4 w-4 mr-1" /> Analytics</TabsTrigger>
          <TabsTrigger value="preferences" data-testid="comm-tab-preferences"><ShieldOff className="h-4 w-4 mr-1" /> Preferences</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="comm-tab-timeline"><UserSearch className="h-4 w-4 mr-1" /> Timeline</TabsTrigger>
        </TabsList>
        <TabsContent value="triggers" className="mt-4"><TriggersTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="queue" className="mt-4"><QueueTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="templates" className="mt-4"><TemplatesTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="hr-digest" className="mt-4"><HRDigestTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="analytics" className="mt-4"><AnalyticsTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="preferences" className="mt-4"><PreferencesTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="timeline" className="mt-4"><TimelineTab tenantId={tenantId} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default SchoolCommunicationEngine;

// ========== PREFERENCES (IMPROVEMENT) ==========
const PreferencesTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data: tenantPref } = useQuery({
    queryKey: ['comm-prefs-tenant', tenantId],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/comm/preferences/tenant`).then(r => r.data),
  });
  const { data: persons = [] } = useQuery({
    queryKey: ['comm-prefs-list', tenantId],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/comm/preferences`).then(r => r.data),
  });
  const [form, setForm] = useState({ quiet_hours_start: '21:00', quiet_hours_end: '07:00', default_channel: 'email', disable_sms_globally: false, disable_whatsapp_globally: false });
  React.useEffect(() => { if (tenantPref) setForm({ ...form, ...tenantPref }); /* eslint-disable-next-line */ }, [tenantPref]);
  const saveTenantMut = useMutation({
    mutationFn: (body) => api.put(`/api/school/tenants/${tenantId}/comm/preferences/tenant`, body),
    onSuccess: () => { toast.success('Tenant preferences saved'); qc.invalidateQueries({ queryKey: ['comm-prefs-tenant', tenantId] }); },
  });
  const delPersonMut = useMutation({
    mutationFn: ({ type, id }) => api.delete(`/api/school/tenants/${tenantId}/comm/preferences/${type}/${id}`),
    onSuccess: () => { toast.success('Preference removed'); qc.invalidateQueries({ queryKey: ['comm-prefs-list', tenantId] }); },
  });
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Clock className="h-4 w-4 text-indigo-600" /> Tenant-Wide Preferences</CardTitle>
          <CardDescription>Quiet hours defer SMS/WhatsApp (emails are always sent). Globally disable channels if carriers are unavailable.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-4 gap-3">
            <div><Label>Quiet start</Label><Input type="time" value={form.quiet_hours_start || ''} onChange={(e) => setForm({ ...form, quiet_hours_start: e.target.value })} data-testid="pref-quiet-start" /></div>
            <div><Label>Quiet end</Label><Input type="time" value={form.quiet_hours_end || ''} onChange={(e) => setForm({ ...form, quiet_hours_end: e.target.value })} data-testid="pref-quiet-end" /></div>
            <div><Label>Default channel</Label>
              <Select value={form.default_channel || 'email'} onValueChange={(v) => setForm({ ...form, default_channel: v })}>
                <SelectTrigger data-testid="pref-default-channel"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="sms">SMS</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.disable_sms_globally} onChange={(e) => setForm({ ...form, disable_sms_globally: e.target.checked })} data-testid="pref-disable-sms" /> Disable SMS</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.disable_whatsapp_globally} onChange={(e) => setForm({ ...form, disable_whatsapp_globally: e.target.checked })} data-testid="pref-disable-whatsapp" /> Disable WhatsApp</label>
            </div>
          </div>
          <Button onClick={() => saveTenantMut.mutate(form)} disabled={saveTenantMut.isPending} data-testid="pref-save-tenant">
            {saveTenantMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save Tenant Preferences
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Per-Person Preferences (Do-Not-Contact)</CardTitle>
          <CardDescription>Block all messaging for specific students, staff, or guardians. Managed from their individual profile; this panel lists current rules.</CardDescription>
        </CardHeader>
        <CardContent>
          {persons.length === 0 ? <p className="text-sm text-slate-500 text-center py-4" data-testid="pref-persons-empty">No per-person preferences set.</p> :
           <Table>
            <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Person</TableHead><TableHead>DNC</TableHead><TableHead>Preferred</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {persons.map(p => (
                <TableRow key={`${p.person_type}-${p.person_id}`} data-testid={`pref-person-${p.person_id}`}>
                  <TableCell><Badge variant="outline">{p.person_type}</Badge></TableCell>
                  <TableCell className="font-medium">{p.person_name || p.person_id}</TableCell>
                  <TableCell>{p.do_not_contact ? <Badge variant="destructive">DNC</Badge> : '—'}</TableCell>
                  <TableCell className="text-xs">{p.preferred_channel || 'any'}</TableCell>
                  <TableCell className="text-xs text-slate-600">{p.note || '—'}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => delPersonMut.mutate({ type: p.person_type, id: p.person_id })} data-testid={`pref-del-${p.person_id}`}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
           </Table>}
        </CardContent>
      </Card>
    </div>
  );
};

// ========== TIMELINE (IMPROVEMENT) ==========
const TimelineTab = ({ tenantId }) => {
  const [personType, setPersonType] = useState('student');
  const [personId, setPersonId] = useState('');
  const [dncForm, setDncForm] = useState({ do_not_contact: false, preferred_channel: '', note: '' });

  const { data, refetch, isFetching } = useQuery({
    queryKey: ['comm-timeline', tenantId, personType, personId],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/comm/timeline?person_type=${personType}&person_id=${personId}`).then(r => r.data),
    enabled: !!personId,
  });

  const dncMut = useMutation({
    mutationFn: (body) => api.put(`/api/school/tenants/${tenantId}/comm/preferences`, body),
    onSuccess: () => toast.success('Preference saved'),
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });

  const msgs = data?.messages || [];
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><UserSearch className="h-4 w-4 text-sky-600" /> Communication Timeline</CardTitle>
          <CardDescription>Every email/SMS/WhatsApp ever sent to or about this person.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-3">
            <Select value={personType} onValueChange={setPersonType}>
              <SelectTrigger className="w-36" data-testid="timeline-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="student">Student</SelectItem><SelectItem value="staff">Staff</SelectItem><SelectItem value="teacher">Teacher</SelectItem><SelectItem value="guardian">Guardian</SelectItem></SelectContent>
            </Select>
            <Input placeholder={`${personType} ID`} value={personId} onChange={(e) => setPersonId(e.target.value)} className="w-72" data-testid="timeline-id" />
            <Button onClick={() => refetch()} disabled={!personId} data-testid="timeline-load">Load Timeline</Button>
            <Button variant="outline" onClick={() => dncMut.mutate({ person_type: personType, person_id: personId, ...dncForm, do_not_contact: true })} disabled={!personId} data-testid="timeline-dnc">Set Do-Not-Contact</Button>
            <Button variant="ghost" onClick={() => dncMut.mutate({ person_type: personType, person_id: personId, ...dncForm, do_not_contact: false })} disabled={!personId} data-testid="timeline-undnc">Clear DNC</Button>
          </div>
          {data && (
            <>
              <div className="grid md:grid-cols-4 gap-3 mb-3">
                <div className="p-2 rounded border bg-indigo-50"><div className="text-[10px] text-indigo-700">Total</div><div className="text-xl font-bold text-indigo-900" data-testid="timeline-total">{data.summary?.total || 0}</div></div>
                <div className="p-2 rounded border bg-emerald-50"><div className="text-[10px] text-emerald-700">Sent</div><div className="text-xl font-bold text-emerald-900">{data.summary?.by_status?.sent || 0}</div></div>
                <div className="p-2 rounded border bg-rose-50"><div className="text-[10px] text-rose-700">Failed</div><div className="text-xl font-bold text-rose-900">{data.summary?.by_status?.failed || 0}</div></div>
                <div className="p-2 rounded border bg-slate-50"><div className="text-[10px] text-slate-700">Resolved contacts</div><div className="text-[11px]">{[...(data.resolved_emails || []), ...(data.resolved_phones || [])].slice(0,2).join(', ') || '—'}</div></div>
              </div>
              {isFetching ? <Loader2 className="h-5 w-5 animate-spin" /> :
               msgs.length === 0 ? <p className="text-sm text-slate-500 text-center py-4" data-testid="timeline-empty">No messages for this person yet.</p> :
               <div className="space-y-2 max-h-[520px] overflow-y-auto">
                {msgs.map(m => (
                  <div key={m.id} className="p-2 rounded-md border bg-white" data-testid={`timeline-msg-${m.id}`}>
                    <div className="flex items-center gap-2 text-xs mb-1">
                      <Badge variant="secondary">{m.channel}</Badge>
                      <Badge variant="outline">{m.trigger}</Badge>
                      <Badge className={STATUS_COLOR[m.status] || ''}>{m.status}</Badge>
                      <span className="text-slate-500 ml-auto">{m.created_at?.slice(0, 16)?.replace('T', ' ')}</span>
                    </div>
                    <div className="text-xs font-medium">{m.subject || m.to_email || m.to_phone}</div>
                    <div className="text-xs text-slate-600 whitespace-pre-wrap">{m.body_preview}</div>
                  </div>
                ))}
               </div>}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
